import { useDispatch, useSelector } from 'react-redux';

import { pushParams, resetParams } from 'dpl/actions/query_params';
import {
  FILTER_GENDER,
  SIMILAR_BREEDS_OPEN,
  SIMILAR_BREEDS_PAGE,
  SIMILAR_BREEDS_RESULTS_TYPE
} from 'dpl/constants/query_params';

import {
  SIMILAR_BREEDS_RESULTS_TYPES,
  SimilarBreedsResultsType
} from '../utils/similarBreeds';

const OVERLAY_PARAMS = [
  SIMILAR_BREEDS_OPEN,
  SIMILAR_BREEDS_RESULTS_TYPE,
  SIMILAR_BREEDS_PAGE
];

/**
 * Open/closed state, tab and page for the "See all" overlay.
 *
 * Kept in the URL hash like the rest of the search page's params, which buys
 * three things for free: the browser's back button closes the overlay, a
 * refresh reopens it, and closing restores the feed underneath at the scroll
 * position the visitor left it.
 */
export default function useSimilarBreedsSeeAll() {
  const dispatch = useDispatch();
  const params = useSelector(({ queryParams }: any) => queryParams);

  const genderFilter =
    params[FILTER_GENDER] !== 'any' ? params[FILTER_GENDER] : null;

  // Per the Figma annotation: a gender filter is honored in the results, and
  // the breeders tab goes away while it is, since a breeder card cannot express
  // one gender's worth of availability.
  const areTabsAvailable = !genderFilter;

  const isOpen = params[SIMILAR_BREEDS_OPEN] === 'true';

  const requestedResultsType = params[SIMILAR_BREEDS_RESULTS_TYPE];
  const resultsType: SimilarBreedsResultsType =
    areTabsAvailable &&
    requestedResultsType === SIMILAR_BREEDS_RESULTS_TYPES.BREEDERS
      ? SIMILAR_BREEDS_RESULTS_TYPES.BREEDERS
      : SIMILAR_BREEDS_RESULTS_TYPES.PUPPIES;

  const page = Math.max(Number(params[SIMILAR_BREEDS_PAGE]) || 1, 1);

  // `undefined` and not `null` throughout: `qs.stringify` renders a null as a
  // dangling `similar_breeds_p=`, while an undefined drops the key outright -
  // which lets one push both set a param and clear another, so the back button
  // steps over one entry per interaction rather than two.
  function open(initialResultsType: SimilarBreedsResultsType) {
    dispatch(
      pushParams(
        {
          [SIMILAR_BREEDS_OPEN]: true,
          [SIMILAR_BREEDS_RESULTS_TYPE]: initialResultsType,
          [SIMILAR_BREEDS_PAGE]: undefined
        },
        { hash: true }
      )
    );
  }

  function close() {
    dispatch(resetParams(...OVERLAY_PARAMS, { hash: true }));
  }

  function setResultsType(newResultsType: SimilarBreedsResultsType) {
    if (newResultsType === resultsType) {
      return;
    }

    // Switching tabs drops the page: page 4 of the breeders tab is not page 4
    // of anything the puppies tab shows.
    dispatch(
      pushParams(
        {
          [SIMILAR_BREEDS_RESULTS_TYPE]: newResultsType,
          [SIMILAR_BREEDS_PAGE]: undefined
        },
        { hash: true }
      )
    );
  }

  function setPage(newPage: number) {
    dispatch(
      pushParams(
        { [SIMILAR_BREEDS_PAGE]: newPage > 1 ? newPage : undefined },
        { hash: true }
      )
    );
  }

  return {
    areTabsAvailable,
    close,
    genderFilter,
    isOpen,
    open,
    page,
    resultsType,
    setPage,
    setResultsType
  };
}
